import { subscribeAppointmentEvents } from "@/lib/realtime/chat-realtime-service";
import { normalizeAppointmentChangePayload } from "@/lib/realtime/appointment-realtime-helpers";
import { getAppointmentFilters } from "@/lib/stores/appointment-filters-store";
import { isNilId } from "@/lib/calendars";

const REFRESH_DEBOUNCE_MS = 150;

const appointmentChangeListeners = new Set();
const appointmentRefreshListeners = new Set();

let isInstalled = false;
let refreshVersion = 0;
let refreshTimer = null;
let pendingRefreshChange = null;

function notifyAppointmentChange(change) {
  for (const listener of appointmentChangeListeners) {
    try {
      listener(change);
    } catch (_error) {
      // ignore listener errors
    }
  }
}

function notifyAppointmentRefresh(change) {
  refreshVersion += 1;
  for (const listener of appointmentRefreshListeners) {
    try {
      listener({
        version: refreshVersion,
        change,
      });
    } catch (_error) {
      // ignore listener errors
    }
  }
}

/**
 * Should this change trigger a calendar refetch under the user's current filters?
 * Nil (empty or all-zero) tenant/doctor ids pass straight through: a colleague's direct
 * entry on a standalone calendar has no doctor/clinic at all, and treating its zero-hex
 * sentinel as "some other clinic" would silently swallow the refetch. A change whose
 * calendar is among the selected standalone calendars also always passes.
 */
export function matchesSelectedFilters(change, filters = getAppointmentFilters()) {
  const tenantIds = Array.isArray(filters?.tenantIds) ? filters.tenantIds : null;
  const doctorIds = Array.isArray(filters?.doctorIds) ? filters.doctorIds : null;
  const calendarIds = Array.isArray(filters?.calendarIds) ? filters.calendarIds : null;

  const calendarId = String(change?.calendarId || "").trim();
  if (calendarId && !isNilId(calendarId) && calendarIds?.includes(calendarId)) {
    return true;
  }

  const tenantId = String(change?.tenantId || "").trim();
  if (tenantIds?.length && !isNilId(tenantId) && !tenantIds.includes(tenantId)) {
    return false;
  }

  const doctorId = String(change?.doctorId || "").trim();
  if (doctorIds?.length && !isNilId(doctorId) && !doctorIds.includes(doctorId)) {
    return false;
  }

  return true;
}

function queueRefresh(change) {
  pendingRefreshChange = change;
  if (refreshTimer !== null) {
    return;
  }

  refreshTimer = setTimeout(() => {
    const nextChange = pendingRefreshChange;
    pendingRefreshChange = null;
    refreshTimer = null;
    notifyAppointmentRefresh(nextChange);
  }, REFRESH_DEBOUNCE_MS);
}

function handleRawAppointmentEvent(rawEvent) {
  const change = normalizeAppointmentChangePayload(rawEvent);
  if (!change) {
    return;
  }

  notifyAppointmentChange(change);

  if (matchesSelectedFilters(change)) {
    queueRefresh(change);
  }
}

function installAppointmentRealtimeBridge() {
  if (isInstalled) {
    return;
  }

  isInstalled = true;
  subscribeAppointmentEvents(handleRawAppointmentEvent);
}

export function getAppointmentRefreshVersion() {
  installAppointmentRealtimeBridge();
  return refreshVersion;
}

export function subscribeAppointmentChanges(listener) {
  installAppointmentRealtimeBridge();
  if (typeof listener !== "function") {
    return () => {};
  }

  appointmentChangeListeners.add(listener);
  return () => {
    appointmentChangeListeners.delete(listener);
  };
}

export function subscribeAppointmentRefresh(listener) {
  installAppointmentRealtimeBridge();
  if (typeof listener !== "function") {
    return () => {};
  }

  appointmentRefreshListeners.add(listener);
  return () => {
    appointmentRefreshListeners.delete(listener);
  };
}

/**
 * Manually trigger a calendar refresh — used by mutations (slot create/update/delete)
 * that want both the calendar grid and any slot list/panel to re-fetch from one
 * source of truth without prop-callback chains.
 */
export function publishCalendarRefresh(change = null) {
  installAppointmentRealtimeBridge();
  notifyAppointmentRefresh(change);
}

export { normalizeAppointmentChangePayload };
